<script setup>
    import __ from '@components/../mixins/locale'
    import axios from 'axios'
    import FormsAdvert from '@components/Forms/Advert.vue'
    import Modal from '@components/Modal.vue'
    import { computed, onMounted, ref } from 'vue'

    const props = defineProps({
        items: {
            type: Array,
            required: false
        },
        user: {
            type: Object,
            required: true
        },
        formOptions: {
            type: Object,
            required: true
        },
        language: {
            type: String,
            required: true
        },
        translations: {
            type: Object,
            required: false
        },
        apiToken: {
            type: String,
            required: true
        }
    });

    const editingId = ref(null);

    const advertStatus = ref(1);

    const advertFormLoaded = ref(false);

    const adverts = ref({});

    const errors = ref(null);

    const loading = ref(false);

    const modalTitle = computed(() => {
        if (advertFormLoaded.value === false) {
            return __('Advert');
        } else if (advertStatus.value == 1 || editingId.value == null) {
            return __('New Advert');
        } else if (advertStatus.value == 2) {
            return __('Draft - Edit Advert');
        } else if (advertStatus.value == 3) {
            return __('Edit Advert');
        }
    });

    const editAdvert = id => {
        editingId.value = id;
        errors.value = null;
    }

    const deleteAdvert = (advertId, index) => {
        axios.delete(`/api/advert/${advertId}?api_token=` + props.apiToken
            ).then(response => {
            adverts.value.splice(index, 1);
            loading.value = false;
        }).catch(caughtErrors => {
            errors.value = caughtErrors.response.data.errors;
        });
    }

    const parseFloatOverride = (str) => {
        return (Math.round(str * 100) / 100).toFixed(2)
    }

    onMounted(() => {
        if (props.items) {
            adverts.value = props.items;
            loading.value = false;
        } else {
            getAdverts();
        }
    });
</script>

<template>
    <div>
        <div class="container">
            <FormsAdvert 
                v-if="adverts.length === 0"
                @editing-id-removed="editingId = $event"
                @advert-status="advertStatus = $event"
                @advert-form-loaded="advertFormLoaded = $event"
                :editing-id="editingId"
                :user="user" 
                :form-options="formOptions" 
                :language="language" 
                :api-token="apiToken"
                >
            </FormsAdvert>
            <Modal 
                v-else
                namespace="new_advert" 
                :title="modalTitle"
                :btn-text="__('New Advert')"
                trigger-btn-classes="btn btn-success"
                @reset="editingId = $event"
                >
                <FormsAdvert 
                    @editing-id-removed="editingId = $event"
                    @advert-status="advertStatus = $event"
                    @advert-form-loaded="advertFormLoaded = $event"
                    :editing-id="editingId"
                    :user="user" 
                    :form-options="formOptions" 
                    :language="language" 
                    :api-token="apiToken"
                    >
                </FormsAdvert>
            </Modal>
            <div v-if="loading">
                <div class="loading-indicator"><div></div><div></div><div></div></div>
            </div>
            <template v-else>
                <div class="card-columns" style="margin-top: 20px">
                    <div class="card" v-for="(advert, index) in adverts">
                        <div class="card-img-top" :style="'position: relative; height: 200px; background-image: url(\'/storage' + JSON.parse(advert.photos[0].medium).src + '\'); background-repeat: no-repeat; background-size: cover; background-position: center'">
                            <template v-if="advert.type == 1">
                                <span class="badge badge-dark" style="position:absolute; bottom: 10px; left: 10px">{{ __('Meets') }}</span>
                            </template>
                            <template v-else-if="advert.type == 2">
                                <span class="badge badge-dark" style="position:absolute; bottom: 10px; left: 10px">{{ __('Services') }}</span>
                            </template>
                            <template v-else-if="advert.type == 3">
                                <span class="badge badge-dark" style="position:absolute; bottom: 10px; left: 10px">{{ __('Items for Sale') }}</span>
                            </template>
                        </div>
                        <button style="position: absolute; top: 5px; left: 5px" @click="editAdvert(advert.id)" type="button" class="btn btn-primary btn-md" data-toggle="modal" data-target="#new_advertmodal">
                            <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="pen" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" style="width: 0.8rem; margin-bottom: 3px;"><path fill="currentColor" d="M290.74 93.24l128.02 128.02-277.99 277.99-114.14 12.6C11.35 513.54-1.56 500.62.14 485.34l12.7-114.22 277.9-277.88zm207.2-19.06l-60.11-60.11c-18.75-18.75-49.16-18.75-67.91 0l-56.55 56.55 128.02 128.02 56.55-56.55c18.75-18.76 18.75-49.16 0-67.91z"></path></svg>
                        </button>
                        <button style="position: absolute; top: 5px; right: 5px" @click="deleteAdvert(advert.id, index)" type="button" class="btn btn-danger btn-md float-right">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" style="width: 0.8rem; margin-bottom: 3px;"><path fill="currentColor" d="M135.2 17.7C140.6 6.8 151.7 0 163.8 0H284.2c12.1 0 23.2 6.8 28.6 17.7L320 32h96c17.7 0 32 14.3 32 32s-14.3 32-32 32H32C14.3 96 0 81.7 0 64S14.3 32 32 32h96l7.2-14.3zM32 128H416V448c0 35.3-28.7 64-64 64H96c-35.3 0-64-28.7-64-64V128zm96 64c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16z"/></svg>
                        </button>
                        <div class="card-body">
                            <h5 class="card-title">
                                <span v-if="!advert.published_at" class="badge badge-primary">{{ __('Draft') }}</span>
                                <span v-else-if="advert.isExpired" class="badge badge-danger">{{ __('Expired') }}</span>
                                {{ advert.title }}
                            </h5>
                            <p class="card-text" v-if="advert.type == 2" v-html="'<strong>' + advert.price[0].service + ':</strong> ' + advert.currencyData.symbol + parseFloatOverride(advert.price[0].price) + '...'"></p>
                            <p class="card-text" v-else-if="advert.type == 3" v-html="advert.currencyData.symbol + parseFloatOverride(advert.price[0].price)"></p>
                            <p v-if="advert.type == 1" class="card-text">
                                {{ advert.content }}
                            </p>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>